import { useContext, useEffect } from "react";
import {
	GoogleLogin,
	GoogleLoginResponse,
	GoogleLoginResponseOffline,
} from "react-google-login";
import GoogleIcon from "@mui/icons-material/Google";
import { gapi } from "gapi-script";
import { User, emptyUser } from "../../../types/entities";
import { Button, CircularProgress } from "@mui/material";
import {
	LoginContext,
	MultistepContext,
	LoginContextValues,
	MultistepContextValues,
	BusinessDataContext,
	BusinessDataContextValues,
} from "../../../types/context";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { FormattedMessage, useIntl } from "react-intl";
import React from "react";
import { UserApi } from "../../../api/userApi";
import { findComponentStep } from "../../../utils/filter";
import { CustomException } from "../../../types/customException";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";

export const StyledCircularProgress: React.FC<{ isMobile: boolean }> = ({
	isMobile,
}) => (
	<CircularProgress
		size={20}
		color="inherit"
		style={{
			margin: isMobile ? "0% 5%" : "0% 5%",
			transform: "translate(-50%, -50%)",
		}}
	/>
);

const Google: React.FC<{ setError: Function }> = ({ setError }) => {
	const clientID =
		"700718116668-2u91tg44c6eru6l81qov06odhfsv07ch.apps.googleusercontent.com";

	const { onNext, onChangeStep, onChangeLogin } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { onLoginWithGoogle, isCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	const { selectedFlow } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	const { isMobile } = useWindowResize();
	const { formatMessage } = useIntl();

	useEffect(() => {
		const start = () => {
			gapi.auth2.init({
				client_id: clientID,
			});
		};

		gapi.load("client:auth2", start);
	}, []);

	const handleLoginGoogle = async (customer: User) => {
		const userApi = new UserApi();
		const errorMessage = await tryCatchHandler(
			async () => {
				let reservationCustomer = await userApi.googleLogin(customer);
				onChangeLogin(reservationCustomer.data);
				onLoginWithGoogle();
				if (
					!reservationCustomer.data.phoneNumber ||
					reservationCustomer.data.phoneNumber === ""
				) {
					const registerStep = findComponentStep(selectedFlow!, "Register");
					onChangeStep(registerStep);
				} else {
					if (isCalledFromProfile) {
						const profileStep = findComponentStep(selectedFlow!, "Profile");
						onChangeStep(profileStep);
					} else {
						onNext();
					}
				}
			},
			"error.googleLogin",
			"Ha ocurrido un error iniciando sesión con Google.",
			formatMessage
		);

		if (errorMessage) setError(errorMessage);
	};

	const onSuccess = (res: GoogleLoginResponse | GoogleLoginResponseOffline) => {
		if ("profileObj" in res) {
			// res is of type GoogleLoginResponse
			const profileObj = (res as GoogleLoginResponse).profileObj;
			const customer = {
				...emptyUser,
				email: profileObj.email,
				name: profileObj.givenName,
				lastname: profileObj.familyName,
			};
			handleLoginGoogle(customer);
		}
	};

	const onFailure = (res: any) => {
		console.error(res);
	};

	return (
		<>
			<GoogleLogin
				clientId={clientID}
				onSuccess={onSuccess}
				onFailure={onFailure}
				cookiePolicy={"single_host_origin"}
				render={(renderProps) => (
					<Button
						onClick={renderProps.onClick}
						disabled={renderProps.disabled}
						variant="contained"
						fullWidth
						startIcon={
							renderProps.disabled ? (
								<StyledCircularProgress isMobile={isMobile} />
							) : (
								<GoogleIcon sx={{ mb: 0.5, mr: 0.5 }} />
							)
						}
						sx={{ height: "50px", mt: 2 }}
					>
						<FormattedMessage
							id="login.google"
							defaultMessage="Iniciar sesión con Google"
						/>
					</Button>
				)}
			/>
		</>
	);
};

export default Google;
